import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import React from 'react'
type RTFOutputProps = {
   text: string
   fontSize?: 300 | 400
   noMargin?: boolean
   className?: string
}

/**
 * Typography for the HTML produced by the TipTap editor.
 *
 * The styles are applied as descendant selectors rather than injected into the markup as
 * inline styles, so they also reach elements that carry attributes of their own — such as
 * the indented `<p style="margin-left: …">` and the lettered `<ol type="a">` the editor emits.
 *
 * Only vertical margins are ever set, never the `margin` shorthand: Griffel expands the
 * shorthand to all four longhands, which would compete with the indent.
 */
const useStyles = makeStyles({
   root: {
      fontWeight: tokens.fontWeightRegular,
      '& a': { color: tokens.colorBrandForegroundLink },
      // TipTap serialises list items as <li><p>…</p></li>; without this every list item
      // would be a full blank line tall.
      '& li > p': { marginTop: 0, marginBottom: 0 },
      '& li > ul': { marginTop: 0, marginBottom: 0 },
      '& li > ol': { marginTop: 0, marginBottom: 0 },
   },
   size300: {
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      '& p': { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
      '& li': { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
   },
   size400: {
      fontSize: tokens.fontSizeBase400,
      lineHeight: tokens.lineHeightBase400,
      '& p': { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
      '& li': { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
   },
   noMargin: {
      '& p': { marginTop: 0, marginBottom: 0 },
      '& li': { marginTop: 0, marginBottom: 0 },
   },
})

export const RTFOutput = ({ text, fontSize, noMargin, className }: RTFOutputProps) => {
   const classes = useStyles()

   return (
      <div
         className={mergeClasses(
            classes.root,
            fontSize === 300 ? classes.size300 : classes.size400,
            noMargin ? classes.noMargin : undefined,
            className
         )}
         dangerouslySetInnerHTML={{ __html: text ?? '' }}
      />
   )
}
